
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { signUp, signIn, resetPassword } from "@/utils/auth";
import { useAuthValidation } from "./use-auth-validation";

interface UseAuthHandlersProps {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  country: string;
  acceptedTerms: boolean;
  setIsLoading: (loading: boolean) => void;
  setIsForgotPassword: (isForgot: boolean) => void;
  isSignUp: boolean;
}

export const useAuthHandlers = ({
  email,
  password,
  firstName,
  lastName,
  country,
  acceptedTerms,
  setIsLoading,
  setIsForgotPassword,
  isSignUp,
}: UseAuthHandlersProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { validateSignUp, validateSignIn, validatePasswordReset } = useAuthValidation();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let isValid = false;
    
    if (isSignUp) {
      isValid = validateSignUp(email, password, firstName, lastName, country, acceptedTerms);
    } else {
      isValid = validateSignIn(email, password);
    }
    
    if (!isValid) return;

    setIsLoading(true);

    try {
      if (isSignUp) {
        // For signup flow
        console.log("Attempting to sign up with:", { email: email.trim(), firstName, lastName, country });
        const result = await signUp({ 
          email: email.trim(), 
          password, 
          firstName, 
          lastName, 
          country,
          metadata: {
            username: email.trim(),
            displayName: `${firstName} ${lastName}`.trim(),
          }
        });
        
        if (result.error) throw result.error;
        
        if (result.needsEmailConfirmation) {
          toast({
            title: "Check your email!",
            description: "We've sent a confirmation link to your email. Please verify your email before signing in.",
          });
          setIsForgotPassword(false);
        } else {
          navigate("/");
          toast({
            title: "Welcome!",
            description: "Your account has been created successfully.",
          });
        }
      } else {
        // For sign in flow
        console.log("Attempting to sign in with:", email.trim());
        const { error } = await signIn(email.trim(), password);
        
        if (error) throw error;
        
        navigate("/");
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      
      let errorMessage = "An error occurred during authentication. Please try again.";
      
      if (error.message && error.message.includes("Email not confirmed")) {
        errorMessage = "Please check your email and click the confirmation link before signing in.";
      } else if (error.message && error.message.includes("invalid_credentials")) {
        errorMessage = "Invalid email or password. Please check your credentials and try again.";
      } else if (error.message && error.message.includes("User already registered")) {
        errorMessage = "This email is already registered. Please sign in instead.";
      } else if (error.message && error.message.includes("Password should be at least 6 characters")) {
        errorMessage = "Password must be at least 6 characters long.";
      } else if (error.message && error.message.includes("Invalid email")) {
        errorMessage = "Please enter a valid email address.";
      } else if (error.message && error.message.includes("weak")) {
        errorMessage = "Password is too weak. Please choose a stronger password.";
      }

      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordReset(email)) {
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await resetPassword(email.trim());
      
      if (error) throw error;
      
      toast({
        title: "Password Reset Link Sent",
        description: "If an account exists with this email, you will receive a password reset link.",
      });
      
      // Return to sign in form after sending reset link
      setIsForgotPassword(false);
      
    } catch (error: any) {
      console.error("Password reset error:", error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while sending the password reset link. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleAuth,
    handleResetPassword
  };
};
